import { useId } from "react";

/**
 * Page-level SEO bridge.
 *
 * In the SPA (React Router) build this component wrote <title>, <meta>,
 * <link rel=canonical>, and JSON-LD <script> tags into <head> via
 * react-helmet-async. After the TanStack Start migration, document <head>
 * is owned by each route's `head()` export (server-rendered), so this
 * component no longer manages <title>/<meta>/<link>.
 *
 * It STILL renders the page-provided JSON-LD (`schemaJson`) as
 * server-rendered <script type="application/ld+json"> tags in the body.
 * JSON-LD is valid in <body> and is read by Google/AI crawlers, so the
 * rich-result data (MedicalWebPage, MedicalTherapy, FAQPage, Article,
 * BreadcrumbList) that each page already passes here ships in the SSR
 * HTML without a per-route head() migration. The <title>/<meta
 * description>/<link canonical> for each route live in that route's
 * `head()` export.
 */
export const SITE_URL = "https://neurowellnesstms.com";

export interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  keywords?: string;
  noIndex?: boolean;
  schemaJson?: object | object[];
}

export default function SEOHead({ schemaJson }: SEOHeadProps) {
  const baseId = useId();
  const graphs: object[] = schemaJson
    ? Array.isArray(schemaJson)
      ? schemaJson
      : [schemaJson]
    : [];

  if (graphs.length === 0) return null;

  return (
    <>
      {graphs.map((graph, i) => (
        <script
          key={`${baseId}-${i}`}
          id={`seo-jsonld-${baseId}-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(graph) }}
        />
      ))}
    </>
  );
}
